import {
  Grid, IconButton, Typography, MenuItem, Button,
} from '@material-ui/core';
import type { AxiosError } from 'axios';
import axios from 'axios';
import * as React from 'react';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import {
  useNavigate,
} from 'react-router-dom';
import type { GridColDef } from '@material-ui/data-grid';
import { DataGrid } from '@material-ui/data-grid';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ActionMenu from '../../global/ActionMenu';
import globalUseStyles from '../../../globalClasses';

interface IEmployeeInfo{
  id:string;
  name: string;
  email: string;
  barcode:string;
  facilities: string[];
  employeeLevels:string[];
  departments: string[];
  isActive: boolean;
}

const ViewUsers: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const globalStyles = globalUseStyles();

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [userId, setUserId] = React.useState<string | null>(null);
  const [pageSize, setPageSize] = React.useState<number>(100);

  const employeeFetcher = (url: string) => axios.get<{ employees:Array<IEmployeeInfo> }>(url)
    .then((response) => response.data.employees)
    .catch((e: AxiosError) => {
      enqueueSnackbar(`There was the following error when fetching the employees: ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
    });

  const { data } = useSWR(
    ['/employeeInfo/GetEmployees'],
    (url) => employeeFetcher(url),
  );

  const handleActionMenuClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    if (!anchorEl) {
      setUserId(id);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setUserId(null);
    setAnchorEl(null);
  };

  const displayVertIcon = (userIdParameter: string) => (
    <IconButton
      aria-label="more"
      aria-controls="long-menu"
      aria-haspopup="true"
      id="table-kabob"
      onClick={(e) => handleActionMenuClick(e, userIdParameter)}
    >
      <MoreVertIcon />
    </IconButton>
  );

  const manualAddColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 0.5 },
    {
      field: 'facilities', headerName: 'Facility', flex: 0.33, renderCell: ({ value }) => (Array.isArray(value) ? value.join(', ') : value),
    },
    {
      field: 'barcode',
      headerName: 'Barcode',
      flex: 0.25,
      renderCell: ({ value }) => ((typeof value === 'string') ? value : <></>),
    },
    {
      field: 'departments',
      headerName: 'Departments',
      flex: 0.75,
      renderCell: ({ value }) => (Array.isArray(value) ? value.join(', ') : value),
    },
    {
      field: 'employeeLevels',
      headerName: 'User Roles',
      flex: 0.25,
      renderCell: ({ value }) => (Array.isArray(value) ? value.join(', ') : value),
    },
    {
      field: 'id',
      type: 'string',
      headerName: 'Actions',
      flex: 0.20,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayVertIcon(value)
          : <></>
        )
      ),
    },
  ];

  return (
    <Grid container spacing={0}>
      <Grid item xs={11} container spacing={3} alignItems="stretch">
        <Grid item xs={10}>
          <Typography variant="h5" color="primary">
            { 'Users' }
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => navigate('/create-user')}
          >
            { 'Add New User' }
          </Button>
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            rows={data || []}
            loading={!data}
            columns={manualAddColumns}
            autoHeight
            isRowSelectable={() => false}
            rowsPerPageOptions={[10, 25, 50, 100]}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            className={globalStyles.transparentDataGrid}
          />
          {
              userId && anchorEl && (
                <ActionMenu
                  anchorEl={anchorEl}
                  handleClose={handleClose}
                >
                  <MenuItem
                    id="test-button"
                    onClick={() => navigate(`/edit-user/${userId}`)}
                  >
                    {'Edit User'}
                  </MenuItem>
                </ActionMenu>
              )
            }

        </Grid>
      </Grid>
    </Grid>
  );
};

export default ViewUsers;
